<template>
  <sf-modal-portal
    modal-id="accept-all-pulse-changes-modal"
    :scrollable-content="true"
    size="s"
  >
    <template #title>{{
      I18n.t('pulse.accept_all_pulse_user_changes')
    }}</template>
    <template #messages>
      <sf-modal-message v-if="failed" :variant="messageTypes.error">
        {{ I18n.t('pulse.accept_all_changes_failed') }}
      </sf-modal-message>

      <sf-modal-message
        v-if="allProposalHaveConflicts"
        :variant="messageTypes.warning"
        >{{ proposalsWithConflictsMessage }}
      </sf-modal-message>
    </template>
    <template #body>
      <div v-if="!allProposalHaveConflicts">
        <p class="sf-below-2">
          {{ message }}
          <span
            v-tooltip="checkChangestooltip"
            class="icon icon-info-circle sf-pad-l-1"
          />
        </p>
      </div>

      <proposal-links
        v-if="proposalsWithoutConflicts.length"
        :proposals="proposalsWithoutConflicts"
      />

      <div
        v-if="proposalsWithConflictsCount && !allProposalHaveConflicts"
        class="sf-layer-rounded sf-layer-border tw-overflow-hidden tw-mt-4"
      >
        <div
          class="
            tw-text-center
            sf-bg-warning-lighter sf-c-warning-dark sf-pad-2
          "
        >
          {{ proposalsWithConflictsMessage }}
        </div>

        <div class="sf-pad-2">
          <proposal-links :proposals="proposalsWithConflicts" />
        </div>
      </div>

      <proposal-links v-else :proposals="proposalsWithConflicts" />
    </template>

    <template v-if="noProposalsHaveConflicts" #messages-bottom>
      <sf-modal-message :variant="messageTypes.neutral">
        <div class="sf-level">
          <sf-toggle :model-value="!block" @click="toggleBlock" />
          <div class="text-left">
            <span> {{ toggleLabel }}</span>

            <span
              v-tooltip="tooltip"
              class="icon icon-info-circle sf-pad-l-1"
            />
          </div>
        </div>
      </sf-modal-message>
    </template>

    <template v-if="!allProposalHaveConflicts" #footer>
      <button class="sf-btn" @click.prevent.stop="close">
        {{ I18n.t('confirmation.cancel') }}
      </button>
      <sf-button
        :variant="messageTypes.success"
        :disabled="submitting"
        @click="save"
      >
        {{ buttonLabel }}
      </sf-button>
    </template>
  </sf-modal-portal>
</template>

<script setup lang="ts">
import {
  SfModalPortal,
  SfModalMessage,
} from '@app/shared/vue/components/sf-modal'
import SfButton from '@app/shared/vue/components/sf-button/sf-button.vue'
import { messageTypes } from '@app/shared/vue/components/sf-modal/constants'
import SfToggle from '@app/shared/vue/components/sf-toggle.vue'
import SFetch from '@app/utils/sfetch'
import { ProposalData } from '../types'
import { computed, ref } from 'vue'
import ProposalLinks from './proposal-links.vue'

const I18n = window.I18n

interface Props {
  proposalsData: Array<ProposalData>
  blocked?: boolean
  workflow: string
  endpoint: string
}
const props = defineProps<Props>()
const submitting = ref(false)
const failed = ref(false)

const message = computed(() =>
  I18n.t('pulse.accept_all_changes_confirmation_message', {
    workflow: props.workflow,
    count: proposalsWithoutConflicts.value.length,
  })
)

const proposalsWithConflictsMessage = computed(() =>
  I18n.t('pulse.accept_all_with_conflicts', {
    count: proposalsWithConflictsCount.value,
  })
)

const proposalsWithConflicts = computed(() =>
  props.proposalsData.filter((proposal) => proposal.count > 1)
)
const proposalsWithoutConflicts = computed(() =>
  props.proposalsData.filter((proposal) => proposal.count === 1)
)

const proposalsWithConflictsCount = computed(
  () => proposalsWithConflicts.value.length
)

const noProposalsHaveConflicts = computed(
  () => proposalsWithConflictsCount.value === 0
)
const allProposalHaveConflicts = computed(
  () => proposalsWithConflictsCount.value === props.proposalsData.length
)

const tooltip = computed(() =>
  I18n.t('reconciliation_proposal.actions.allow_further_changes_tooltip')
)

const checkChangestooltip = computed(() =>
  I18n.t('reconciliation_proposal.actions.check_changes_tooltip')
)

const toggleLabel = computed(() =>
  I18n.t('reconciliation_proposal.actions.allow_further_changes', {
    item: props.workflow,
  })
)
const block = ref(props.blocked)
function toggleBlock() {
  block.value = !block.value
}

const buttonLabel = computed(() => {
  if (noProposalsHaveConflicts.value) {
    return block.value
      ? I18n.t(
          'reconciliation_proposal.actions.accept_and_block_further_proposals'
        )
      : I18n.t(
          'reconciliation_proposal.actions.accept_and_allow_further_proposals'
        )
  }

  return I18n.t('reconciliation_proposal.actions.accept_some_proposals', {
    count: proposalsWithoutConflicts.value.length,
  })
})

function close() {
  window.modalCitizen.closeModalPortal()
}

const save = async () => {
  submitting.value = true
  failed.value = false
  try {
    await SFetch.json(props.endpoint, {
      method: 'POST',
      body: {
        accept_all_proposed_changes: {
          block: block.value,
          ids: proposalsWithoutConflicts.value.map((proposal) => proposal.id),
        },
      },
    })
    window.Turbolinks.visit(window.location.href)
  } catch (_) {
    failed.value = true
  } finally {
    submitting.value = false
  }
}
</script>
